import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { auth } from '../core/auth.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { countryFieldHtml, addressPasteFieldHtml, wireAddressPaste } from '../components/PropertyBasicsFields.js';

function roleLabel(role) {
  const key = `role.${role}`;
  const label = t(key);
  return label === key ? role : label;
}

// HU tax number: 12345678-1-41
const HU_TAX_RE = /^[0-9]{8}-[0-9]-[0-9]{2}$/;
// HU GIRO bank account: 8-8 or 8-8-8 digits
const HU_GIRO_RE = /^[0-9]{8}-[0-9]{8}(-[0-9]{8})?$/;
// IBAN: country (2 letters) + 2 check digits + up to 30 alphanumerics; spaces allowed in input
const IBAN_RE = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/;

export function validateBillingFields({ billing_tax_number, billing_bank_account }) {
  const errors = {};
  const tax = (billing_tax_number || '').trim();
  if (tax !== '' && !HU_TAX_RE.test(tax)) errors.billing_tax_number = 'me.billing_tax_number_invalid';
  const bank = (billing_bank_account || '').trim();
  if (bank !== '') {
    const compact = bank.replace(/\s+/g, '').toUpperCase();
    if (!HU_GIRO_RE.test(bank) && !IBAN_RE.test(compact)) {
      errors.billing_bank_account = 'me.billing_bank_account_invalid';
    }
  }
  return errors;
}

export class MeView extends Component {
  constructor() {
    super();
    this.state = {
      invitations: null,
      loadError: null,
      busyId: null,
      revealed: {},
      error: null,
      hardRefreshBusy: false,
      settingsBusy: false,
      settingsNotice: null,
      settingsError: null,
      billingBusy: false,
      billingNotice: null,
      billingError: null,
      billingFieldErrors: {},
      bankAccounts: null,
      bankBusy: false,
      bankNotice: null,
      bankError: null,
      bankAdding: false,
    };
  }

  template() {
    const u = auth.user;
    if (!u) return `<div class="muted">${esc(t('me.not_signed_in'))}</div>`;
    // Billing / bank / marketplace / invite sections are always shown — a
    // brand-new account must set them up before its first property exists.
    // Only the /admin/ocr-logs link genuinely requires an operator account.
    const hasOcrLogsAccess = u.has_properties !== false;
    const initials = this._initialsFor(u);
    const isAgencyStaff = (u.roles || []).includes('AGENCY_STAFF');
    const billingReady = !!(u.billing_legal_name && u.billing_address_line1);
    const bankCount = Array.isArray(this.state.bankAccounts) ? this.state.bankAccounts.length : null;
    const pendingCount = Array.isArray(this.state.invitations)
      ? this.state.invitations.filter((i) => i.status === 'PENDING').length : null;
    return `
      <section class="stack">
        <div class="panel">
          <div class="panel-head">
            <div class="panel-num"><span class="panel-num-mono">${esc(initials)}</span></div>
            <div class="panel-titles">
              <div class="panel-title-eyebrow">${esc(t('me.eyebrow') || 'Account · Profilom')}</div>
              <h2 class="panel-title">${esc(u.full_name || u.email)}${isAgencyStaff ? ` <span class="panel-title-acc">${esc(roleLabel('AGENCY_STAFF'))}</span>` : ''}</h2>
            </div>
            <div class="panel-status">
              <span class="badge primary"><span class="dot-anim"></span>${esc(t('me.signed_in') || 'Bejelentkezve')}</span>
            </div>
          </div>
          <div class="panel-body">
            <p class="panel-lede">${esc(t('me.lede') || 'Itt kezelheted a profilod, számlázási adataidat és meghívásaidat.')}</p>
            ${this._statsHtml({ billingReady, bankCount, pendingCount })}
            <div class="wizard-cols">
              <div class="wizard-col-main stack">
                ${this._becomeLandlordSectionHtml()}
                ${this._billingSectionHtml(u)}
                ${this._bankSectionHtml()}
                ${this._invitationsSectionHtml()}
                ${this._settingsSectionHtml(u)}
                ${this._actionsSectionHtml()}
              </div>
              <aside class="wizard-col-side stack">
                ${this._identityNoteHtml(u)}
                ${this._shortcutsNoteHtml(hasOcrLogsAccess)}
              </aside>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  _statsHtml({ billingReady, bankCount, pendingCount }) {
    const tile = (label, value, hint = '') => `
      <div class="me-stat">
        <div class="me-stat-l">${esc(label)}</div>
        <div class="me-stat-v">${esc(value)}</div>
        ${hint ? `<div class="me-stat-h">${esc(hint)}</div>` : ''}
      </div>`;
    const tiles = [];
    tiles.push(tile(
      t('me.stats.billing') || 'Számlázás',
      billingReady ? (t('me.stats.ready') || 'Kész') : (t('me.stats.incomplete') || 'Hiányos'),
      billingReady ? (t('me.stats.billing_ready_hint') || 'Számlák kibocsáthatók') : (t('me.stats.billing_missing_hint') || 'Add meg a számlázási nevet és címet')
    ));
    tiles.push(tile(
      t('me.stats.bank_accounts') || 'Bankszámlák',
      bankCount === null ? '—' : String(bankCount),
      bankCount && bankCount > 0 ? (t('me.stats.bank_ready_hint') || 'Beérkező utalások készen állnak') : (t('me.stats.bank_missing_hint') || 'Adj hozzá egyet a számlákhoz')
    ));
    tiles.push(tile(
      t('me.stats.invitations') || 'Meghívók',
      pendingCount === null ? '—' : String(pendingCount),
      t('me.stats.invitations_hint') || 'Még el nem fogadottak'
    ));
    return `<div class="me-stats">${tiles.join('')}</div>`;
  }

  _initialsFor(u) {
    const name = (u.full_name || u.email || '').trim();
    if (!name) return '·';
    const parts = name.split(/[\s.@]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (parts[0] || '·').slice(0, 2).toUpperCase();
  }

  _identityNoteHtml(u) {
    const roleBadges = (u.roles || []).map((r) =>
      `<span class="badge">${esc(roleLabel(r))}</span>`).join(' ');
    return `
      <div class="note">
        <span class="note-l">${esc(t('me.identity_label') || 'Identity')}</span>
        <div class="stack-2" style="margin-top: 6px;">
          <div><span class="faint">${esc(t('me.email'))}</span><br>${esc(u.email)}</div>
          ${roleBadges ? `<div><span class="faint">${esc(t('me.roles'))}</span><br>${roleBadges}</div>` : ''}
          <div><span class="faint">${esc(t('me.user_id'))}</span><br><code class="mono" style="font-size: 11px;">${esc(u.id)}</code></div>
        </div>
      </div>
    `;
  }

  _shortcutsNoteHtml(hasOcrLogsAccess) {
    const link = (href, iconName, label) => `
      <a class="me-shortcut" href="#${esc(href)}">
        ${icon(iconName, { class: 'icon-faint' })}
        <span class="grow"><strong>${esc(label)}</strong></span>
        ${icon('chevron-right', { class: 'icon-faint' })}
      </a>`;
    return `
      <div class="note accent">
        <span class="note-l">${esc(t('me.shortcuts_title'))}</span>
        <div class="stack-2" style="margin-top:8px;">
          ${link('/notifications', 'bell', t('me.shortcut_notifications'))}
          ${hasOcrLogsAccess ? link('/agency', 'building', t('me.shortcut_agency')) : ''}
          ${hasOcrLogsAccess ? link('/admin/ocr-logs', 'file-text', t('me.ocr_logs.button')) : ''}
        </div>
      </div>
    `;
  }

  _actionsSectionHtml() {
    const busy = this.state.hardRefreshBusy;
    return `
      <div class="me-section">
        <div class="section-head">
          <div class="section-head-titles">
            <span class="section-head-eyebrow">${esc(t('me.actions.eyebrow') || 'Karbantartás · Maintenance')}</span>
          </div>
        </div>
        <div class="me-action-row">
          <div class="me-action-text">
            <strong>${esc(t('me.hard_refresh.title'))}</strong>
            <span class="faint">${esc(t('me.hard_refresh.subtitle'))}</span>
          </div>
          <button type="button" class="btn-mono" data-action="hard-refresh" ${busy ? 'disabled' : ''}>
            ${icon('refresh-cw', { size: 'sm' })}<span>${esc(busy ? t('me.hard_refresh.busy') : t('me.hard_refresh.button'))}</span>
          </button>
        </div>
        <div class="me-action-row show-mobile-only">
          <div class="me-action-text">
            <strong>${esc(t('app.logout'))}</strong>
            <span class="faint">${esc(t('me.signout_subtitle') || 'Munkamenet lezárása ezen a készüléken')}</span>
          </div>
          <button type="button" class="btn-mono danger" data-action="sign-out">
            ${icon('log-out', { size: 'sm' })}<span>${esc(t('app.logout'))}</span>
          </button>
        </div>
      </div>
    `;
  }

  _billingSectionHtml(u) {
    const busy = this.state.billingBusy;
    const fieldErr = this.state.billingFieldErrors || {};
    const taxErr = fieldErr.billing_tax_number ? t(fieldErr.billing_tax_number) : null;
    const entity = u.billing_entity_type || 'individual';
    const isCompany = entity === 'company';
    const taxLabel = isCompany ? t('me.billing_adoszam') : t('me.billing_adoazonosito');
    const taxHint = isCompany ? t('me.billing_adoszam_hint') : t('me.billing_adoazonosito_hint');
    const ready = !!(u.billing_legal_name && u.billing_address_line1);
    return `
      <div class="me-section">
      <div class="section-head">
        <div class="section-head-titles">
          <span class="section-head-eyebrow">${esc(t('me.billing_eyebrow') || 'Számlázás · Billing')}</span>
        </div>
        <span class="section-head-count">${esc(ready ? (t('me.stats.ready') || 'Kész') : (t('me.stats.incomplete') || 'Hiányos'))}</span>
      </div>
      <form class="me-form" data-role="billing-form" novalidate>
        <div class="stack-2">
          <h3>${esc(t('me.billing_title'))}</h3>
          <span class="faint">${esc(t('me.billing_help'))}</span>
        </div>
        <fieldset class="field" style="border:none; padding:0; margin:0">
          <legend class="field-label">${esc(t('me.billing_entity_type'))}</legend>
          <div class="seg-radio">
            <label class="seg-radio-opt">
              <input type="radio" name="billing_entity_type" value="individual" ${entity === 'individual' ? 'checked' : ''} />
              <span>${esc(t('me.billing_entity_individual'))}</span>
            </label>
            <label class="seg-radio-opt">
              <input type="radio" name="billing_entity_type" value="company" ${isCompany ? 'checked' : ''} />
              <span>${esc(t('me.billing_entity_company'))}</span>
            </label>
          </div>
        </fieldset>
        <div class="field">
          <label>${esc(t('me.billing_legal_name'))}</label>
          <input class="input" type="text" name="billing_legal_name" maxlength="190"
                 value="${esc(u.billing_legal_name || '')}" />
          <span class="faint">${esc(t('me.billing_legal_name_hint'))}</span>
        </div>
        ${addressPasteFieldHtml()}
        <div class="field-row three">
          <div class="field"><label>${esc(t('me.billing_postal_code'))}</label>
            <input class="input" type="text" name="billing_postal_code" maxlength="32"
                   value="${esc(u.billing_postal_code || '')}" /></div>
          <div class="field"><label>${esc(t('me.billing_city'))}</label>
            <input class="input" type="text" name="billing_city" maxlength="128"
                   value="${esc(u.billing_city || '')}" /></div>
          <div class="field"><label>${esc(t('me.billing_country'))}</label>
            ${countryFieldHtml(u.billing_country || 'HU', { name: 'billing_country' })}</div>
        </div>
        <div class="field">
          <label>${esc(t('me.billing_address_line1'))}</label>
          <input class="input" type="text" name="billing_address_line1" maxlength="255"
                 placeholder="${esc(t('me.billing_address_line1_placeholder'))}"
                 value="${esc(u.billing_address_line1 || '')}" />
        </div>
        <div class="field">
          <label>${esc(t('me.billing_address_line2'))}</label>
          <input class="input" type="text" name="billing_address_line2" maxlength="255"
                 placeholder="${esc(t('me.billing_address_line2_placeholder'))}"
                 value="${esc(u.billing_address_line2 || '')}" />
        </div>
        <div class="field">
          <label>${esc(taxLabel)}</label>
          <input class="input" type="text" name="billing_tax_number" maxlength="64"
                 value="${esc(u.billing_tax_number || '')}" />
          <span class="faint">${esc(taxHint)}</span>
          ${taxErr ? `<span class="error-text">${esc(taxErr)}</span>` : ''}
        </div>
        ${isCompany ? `
          <div class="field-row">
            <div class="field">
              <label>${esc(t('me.billing_company_reg'))}</label>
              <input class="input" type="text" name="billing_company_reg" maxlength="64"
                     value="${esc(u.billing_company_reg || '')}" />
              <span class="faint">${esc(t('me.billing_company_reg_hint'))}</span>
            </div>
            <div class="field">
              <label>${esc(t('me.billing_eu_tax_number'))}</label>
              <input class="input" type="text" name="billing_eu_tax_number" maxlength="64"
                     value="${esc(u.billing_eu_tax_number || '')}" />
              <span class="faint">${esc(t('me.billing_eu_tax_number_hint'))}</span>
            </div>
          </div>
        ` : ''}
        ${this.state.billingError ? `<div class="error-banner">${esc(this.state.billingError)}</div>` : ''}
        ${this.state.billingNotice ? `<div class="faint">${esc(this.state.billingNotice)}</div>` : ''}
        <div class="row" style="justify-content:flex-end">
          <button class="btn btn-primary" type="submit" ${busy ? 'disabled' : ''}>${esc(t('me.billing_save'))}</button>
        </div>
      </form>
      </div>
    `;
  }

  _bankSectionHtml() {
    const rows = this.state.bankAccounts;
    const busy = this.state.bankBusy;
    const err = this.state.bankError;
    const notice = this.state.bankNotice;
    const adding = !!this.state.bankAdding;
    const countLabel = rows === null ? '—' : String(rows.length);
    return `
      <div class="me-section" data-role="bank-accounts">
        <div class="section-head">
          <div class="section-head-titles">
            <span class="section-head-eyebrow">${esc(t('me.bank_accounts.title'))}</span>
            <span class="faint">${esc(t('me.bank_accounts.subtitle'))}</span>
          </div>
          <div class="row" style="gap:var(--space-2); align-items:center">
            <span class="section-head-count">${esc(countLabel)}</span>
            ${adding ? '' : `<button class="btn-mono" type="button" data-action="bank-add-open">${icon('plus', { size: 'sm' })}<span>${esc(t('me.bank_accounts.add'))}</span></button>`}
          </div>
        </div>
        ${err ? `<div class="error-banner">${esc(err)}</div>` : ''}
        ${notice ? `<div class="faint">${esc(notice)}</div>` : ''}
        ${adding ? this._bankAddFormHtml(busy) : ''}
        ${rows === null
          ? `<div class="muted">${esc(t('me.bank_accounts.loading'))}</div>`
          : (rows.length === 0
              ? (adding ? '' : `<button type="button" class="add-tile add-tile-empty" data-action="bank-add-open">
                  <span class="add-tile-plus">+</span>
                  <span class="add-tile-label"><strong>${esc(t('me.bank_accounts.empty'))}</strong></span>
                  <span class="add-tile-hint">${esc(t('me.bank_accounts.add'))}</span>
                </button>`)
              : `<div class="list">${rows.map((r) => this._bankRowHtml(r)).join('')}</div>`)}
      </div>
    `;
  }

  _bankRowHtml(r) {
    const meta = [r.bank_name || '', r.currency || '', r.swift_bic ? `SWIFT ${r.swift_bic}` : '']
      .filter(Boolean).join(' · ');
    return `
      <div class="list-item" data-bank-id="${esc(r.id)}">
        ${icon('credit-card', { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(r.label || r.bank_name || t('me.bank_accounts.untitled'))}${r.is_primary ? ` <span class="badge badge-success">${esc(t('me.bank_accounts.primary'))}</span>` : ''}</strong>
          <span class="mono">${esc(r.account_number)}</span>
          ${meta ? `<span class="faint">${esc(meta)}</span>` : ''}
        </div>
        <div class="row" style="gap:var(--space-2)">
          ${r.is_primary ? '' : `<button class="btn btn-ghost btn-sm" type="button" data-action="bank-primary" data-id="${esc(r.id)}">${esc(t('me.bank_accounts.set_primary'))}</button>`}
          <button class="btn btn-ghost btn-icon" type="button" data-action="bank-delete" data-id="${esc(r.id)}" aria-label="${esc(t('common.delete'))}">${icon('trash', { size: 'sm' })}</button>
        </div>
      </div>
    `;
  }

  _bankAddFormHtml(busy) {
    return `
      <form class="me-form me-bank-form" data-role="bank-add-form">
        <div class="field-row">
          <div class="field"><label>${esc(t('me.bank_accounts.form.label'))}</label>
            <input class="input" name="label" maxlength="120" placeholder="${esc(t('me.bank_accounts.form.label_placeholder'))}" /></div>
          <div class="field"><label>${esc(t('me.bank_accounts.form.currency'))}</label>
            <input class="input" name="currency" maxlength="3" pattern="[A-Za-z]{3}" placeholder="HUF" value="HUF" /></div>
        </div>
        <div class="field">
          <label>${esc(t('me.bank_accounts.form.account_number'))}</label>
          <input class="input" type="text" name="account_number" maxlength="64" required
                 placeholder="${esc(t('me.bank_accounts.form.account_number_placeholder'))}" />
          <span class="faint">${esc(t('me.bank_accounts.form.account_number_hint'))}</span>
        </div>
        <div class="field-row">
          <div class="field"><label>${esc(t('me.bank_accounts.form.bank_name'))}</label>
            <input class="input" name="bank_name" maxlength="160" /></div>
          <div class="field"><label>${esc(t('me.bank_accounts.form.swift_bic'))}</label>
            <input class="input" name="swift_bic" maxlength="16" /></div>
        </div>
        <label class="row" style="gap:var(--space-2)">
          <input type="checkbox" name="is_primary" />
          <span>${esc(t('me.bank_accounts.form.set_primary'))}</span>
        </label>
        <div class="row" style="gap:var(--space-3); justify-content:flex-end">
          <button class="btn btn-ghost" type="button" data-action="bank-add-cancel">${esc(t('common.cancel'))}</button>
          <button class="btn btn-primary" type="submit" ${busy ? 'disabled' : ''}>${esc(t('me.bank_accounts.form.submit'))}</button>
        </div>
      </form>
    `;
  }

  // Shown only to "pure tenants" (no property, holds a tenancy, not opted in):
  // a one-click, self-serve opt-in into landlord features. Once enabled the
  // landlord nav + property creation unlock and we send them straight to the
  // new-property wizard.
  _becomeLandlordSectionHtml() {
    if (auth.canManageProperties) return '';
    const busy = !!this.state.settingsBusy;
    return `
      <div class="me-section" data-role="landlord-card">
        <div class="section-head">
          <div class="section-head-titles">
            <span class="section-head-eyebrow">${esc(t('me.landlord.title'))}</span>
          </div>
        </div>
        <p class="faint">${esc(t('me.landlord.help'))}</p>
        <button type="button" class="btn btn-primary" data-action="become-landlord" ${busy ? 'disabled' : ''}>
          ${icon('building', { size: 'sm' })}<span>${esc(t('me.landlord.cta'))}</span>
        </button>
        ${this.state.settingsError ? `<div class="error-banner">${esc(this.state.settingsError)}</div>` : ''}
      </div>
    `;
  }

  _settingsSectionHtml(u) {
    const enabled = !!u.marketplace_enabled;
    return `
      <div class="me-section" data-role="settings-card">
        <div class="section-head">
          <div class="section-head-titles">
            <span class="section-head-eyebrow">${esc(t('me.settings_title'))}</span>
          </div>
        </div>
        <label class="toggle toggle-block">
          <span class="toggle-label">
            <strong>${esc(t('me.marketplace_label'))}</strong>
            <span class="faint">${esc(t('me.marketplace_help'))}</span>
          </span>
          <span style="display:inline-flex; align-items:center">
            <input type="checkbox" data-action="toggle-marketplace" ${enabled ? 'checked' : ''} ${this.state.settingsBusy ? 'disabled' : ''} />
            <span class="toggle-track"></span>
          </span>
        </label>
        ${this.state.settingsError ? `<div class="error-banner">${esc(this.state.settingsError)}</div>` : ''}
        ${this.state.settingsNotice ? `<div class="faint">${esc(this.state.settingsNotice)}</div>` : ''}
      </div>
    `;
  }

  _invitationsSectionHtml() {
    const head = (countLabel) => `
      <div class="section-head">
        <div class="section-head-titles">
          <span class="section-head-eyebrow">${esc(t('me.pending_invitations'))}</span>
        </div>
        ${countLabel ? `<span class="section-head-count">${esc(countLabel)}</span>` : ''}
      </div>`;
    if (this.state.invitations === null && !this.state.loadError) {
      return `<div class="me-section">${head('')}<div class="muted">${esc(t('common.loading'))}</div></div>`;
    }
    if (this.state.loadError) {
      return `<div class="me-section">${head('')}<div class="error-banner">${esc(this.state.loadError)}</div></div>`;
    }
    const pending = (this.state.invitations || []).filter((i) => i.status === 'PENDING');
    const recent = (this.state.invitations || []).filter((i) => i.status !== 'PENDING').slice(0, 5);
    return `
      <div class="me-section">
        ${head(String(pending.length))}
        ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
        ${pending.length === 0 ? `<div class="empty">${esc(t('me.no_pending'))}</div>` : `<div class="list">${pending.map((i) => this._inviteRowHtml(i)).join('')}</div>`}
        ${recent.length > 0 ? `
          <div class="section-head" style="margin-top: 18px;">
            <div class="section-head-titles">
              <span class="section-head-eyebrow">${esc(t('me.recent'))}</span>
            </div>
          </div>
          <div class="list">${recent.map((i) => this._historyRowHtml(i)).join('')}</div>` : ''}
      </div>
    `;
  }

  _inviteRowHtml(inv) {
    const reveal = this.state.revealed[inv.id];
    const url = reveal ? `${window.location.origin}/#/invite/${reveal.token}` : null;
    const busy = this.state.busyId === inv.id;
    const label = inv.full_name || inv.email || t('me.unnamed');
    const drafts = inv.draft_tenancies_count || 0;
    return `
      <div class="card stack-2">
        <div class="row" style="align-items:center; gap:var(--space-2)">
          ${icon('user', { class: 'list-leading icon-faint' })}
          <div class="stack-2 grow">
            <strong>${esc(label)}</strong>
            <span class="faint">${esc(inv.email || '—')} · ${esc(t('me.expires', { date: inv.expires_at }))}</span>
          </div>
          ${drafts > 0 ? `<span class="badge badge-warning">${esc(t(drafts === 1 ? 'me.draft_tenancy_one' : 'me.draft_tenancy_other', { count: drafts }))}</span>` : ''}
        </div>
        ${reveal ? `
          <div class="row" style="gap:var(--space-2); align-items:center">
            <input class="input mono" readonly value="${esc(url)}" data-role="invite-url" data-invite-id="${esc(inv.id)}" />
            <button type="button" class="btn btn-ghost" data-action="copy" data-invite-id="${esc(inv.id)}">${esc(t('common.copy'))}</button>
          </div>
          <span class="faint">${esc(t('me.new_link_valid', { date: reveal.expires_at }))}</span>
        ` : ''}
        <div class="row" style="gap:var(--space-2); justify-content:flex-end">
          <button type="button" class="btn btn-ghost" data-action="regenerate" data-invite-id="${esc(inv.id)}" ${busy ? 'disabled' : ''}>${busy ? '…' : esc(t('me.resend_link'))}</button>
          <button type="button" class="btn btn-ghost" data-action="revoke" data-invite-id="${esc(inv.id)}" ${busy ? 'disabled' : ''}>${icon('trash', { size: 'sm' })}<span>${esc(t('me.revoke'))}</span></button>
        </div>
      </div>
    `;
  }

  _historyRowHtml(inv) {
    const label = inv.full_name || inv.email || t('me.unnamed');
    const klass = inv.status === 'ACCEPTED' ? 'badge-success' : '';
    return `
      <div class="list-item">
        ${icon('user', { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(label)}</strong>
          <span class="faint">${esc(inv.email || '—')}</span>
        </div>
        <span class="badge ${klass}">${esc(inv.status)}</span>
      </div>
    `;
  }

  mount() {
    const u = auth.user;
    if (!u) return;
    if (this.state.invitations === null && !this.state.loadError) {
      this._loadInvitations();
    }
    const on = (sel, evt, fn) => this.el.querySelectorAll(sel).forEach((el) => el.addEventListener(evt, fn));
    on('[data-action="regenerate"]', 'click', (e) => this._regenerate(e.currentTarget.getAttribute('data-invite-id')));
    on('[data-action="revoke"]', 'click', (e) => this._revoke(e.currentTarget.getAttribute('data-invite-id')));
    on('[data-action="copy"]', 'click', (e) => this._copy(e.currentTarget.getAttribute('data-invite-id')));
    on('[data-action="toggle-marketplace"]', 'change', (e) => this._toggleMarketplace(e.currentTarget.checked));
    on('[data-action="become-landlord"]', 'click', () => this._becomeLandlord());
    on('[data-action="sign-out"]', 'click', () => this._signOut());
    on('[data-action="hard-refresh"]', 'click', () => this._hardRefresh());
    const billingForm = this.el.querySelector('[data-role="billing-form"]');
    if (billingForm) {
      billingForm.addEventListener('submit', (e) => { e.preventDefault(); this._saveBilling(billingForm); });
      // Smart address paste: fills billing_postal_code / billing_city /
      // billing_address_line1 from a single pasted line.
      wireAddressPaste(billingForm);
      // Re-render when entity_type changes so the conditional company-only
      // fields (cégjegyzékszám, EU adószám) appear/disappear immediately
      // and the tax label switches between adóazonosító and adószám.
      billingForm.querySelectorAll('input[name="billing_entity_type"]').forEach((inp) => {
        inp.addEventListener('change', (e) => {
          auth.patchUser({ billing_entity_type: e.target.value });
          this.setState({});
        });
      });
    }
    on('[data-action="bank-add-open"]', 'click', () => this.setState({ bankAdding: true, bankError: null, bankNotice: null }));
    on('[data-action="bank-add-cancel"]', 'click', () => this.setState({ bankAdding: false, bankError: null }));
    on('[data-action="bank-primary"]', 'click', (e) => this._setBankPrimary(e.currentTarget.getAttribute('data-id')));
    on('[data-action="bank-delete"]', 'click', (e) => this._deleteBank(e.currentTarget.getAttribute('data-id')));
    const bankForm = this.el.querySelector('[data-role="bank-add-form"]');
    if (bankForm) bankForm.addEventListener('submit', (e) => { e.preventDefault(); this._addBank(bankForm); });
    if (this.state.bankAccounts === null) this._loadBankAccounts();
  }

  async _signOut() {
    try { await api.post('/auth/logout'); } catch (_) {}
    auth.clear();
    window.location.hash = '#/login';
  }

  async _hardRefresh() {
    if (this.state.hardRefreshBusy) return;
    if (!window.confirm(t('me.hard_refresh.confirm'))) return;
    this.setState({ hardRefreshBusy: true });
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
      }
    } catch (_) { /* best-effort — reload anyway */ }
    // Bypass-cache reload: query bust + full navigation so the SW can't intercept.
    const url = new URL(window.location.href);
    url.searchParams.set('_r', String(Date.now()));
    window.location.replace(url.toString());
  }

  async _toggleMarketplace(enabled) {
    if (this.state.settingsBusy) return;
    this.setState({ settingsBusy: true, settingsError: null, settingsNotice: null });
    try {
      const res = await api.patch('/me/settings', { marketplace_enabled: enabled });
      auth.patchUser({ marketplace_enabled: !!res.marketplace_enabled });
      this.setState({ settingsBusy: false, settingsNotice: t('me.marketplace_saved') });
    } catch (err) {
      this.setState({
        settingsBusy: false,
        settingsError: err instanceof ApiError ? err.message : t('me.marketplace_save_error'),
      });
    }
  }

  async _becomeLandlord() {
    if (this.state.settingsBusy) return;
    this.setState({ settingsBusy: true, settingsError: null, settingsNotice: null });
    try {
      const res = await api.patch('/me/settings', { landlord_enabled: true });
      // Patch the store so the landlord nav + dashboard unlock immediately
      // (AppShell re-renders on the auth change), then jump into the wizard.
      auth.patchUser({ landlord_enabled: !!res.landlord_enabled });
      window.location.hash = '#/properties/new';
    } catch (err) {
      this.setState({
        settingsBusy: false,
        settingsError: err instanceof ApiError ? err.message : t('me.landlord.error'),
      });
    }
  }

  async _saveBilling(form) {
    if (this.state.billingBusy) return;
    const f = form.elements;
    const v = (name) => (f[name] ? f[name].value : '');
    const entity = (f.billing_entity_type && f.billing_entity_type.value) || 'individual';
    const payload = {
      billing_entity_type: entity,
      billing_legal_name: v('billing_legal_name'),
      billing_country: v('billing_country').toUpperCase(),
      billing_postal_code: v('billing_postal_code'),
      billing_city: v('billing_city'),
      billing_address_line1: v('billing_address_line1'),
      billing_address_line2: v('billing_address_line2'),
      billing_tax_number: v('billing_tax_number'),
    };
    if (entity === 'company') {
      payload.billing_company_reg = v('billing_company_reg');
      payload.billing_eu_tax_number = v('billing_eu_tax_number');
    } else {
      // Clear company-only fields when switching back to individual so
      // stale data doesn't keep showing up on invoices.
      payload.billing_company_reg = '';
      payload.billing_eu_tax_number = '';
    }
    // Keep the legacy single-line billing_address in sync as a best-effort
    // pre-rendered string so older invoice templates still find a usable
    // value while the renderer migrates to the structured fields.
    payload.billing_address = [
      payload.billing_address_line1,
      [payload.billing_postal_code, payload.billing_city].filter(Boolean).join(' '),
      payload.billing_address_line2,
      payload.billing_country,
    ].filter((s) => s && s.trim() !== '').join(', ');
    this.setState({ billingBusy: true, billingError: null, billingNotice: null, billingFieldErrors: {} });
    try {
      const res = await api.patch('/me/billing', payload);
      auth.patchUser(res);
      this.setState({ billingBusy: false, billingNotice: t('me.billing_saved') });
    } catch (err) {
      const detail = err instanceof ApiError
        ? `${err.message} (${err.status})`
        : (err && err.message ? err.message : 'unknown error');
      this.setState({
        billingBusy: false,
        billingError: `${t('me.billing_save_error')} — ${detail}`,
      });
    }
  }

  async _loadBankAccounts() {
    try {
      const res = await api.get('/me/bank-accounts');
      this.setState({ bankAccounts: res.data || [], bankError: null });
    } catch (err) {
      const detail = err instanceof ApiError
        ? `${err.message} (${err.status})`
        : (err && err.message ? `${err.message}` : 'unknown error');
      this.setState({
        bankAccounts: [],
        bankError: `${t('me.bank_accounts.errors.load')} — ${detail}`,
      });
    }
  }

  async _addBank(form) {
    if (this.state.bankBusy) return;
    const f = form.elements;
    const payload = {
      label: f.label.value.trim() || null,
      account_number: f.account_number.value.trim(),
      bank_name: f.bank_name.value.trim() || null,
      swift_bic: f.swift_bic.value.trim() || null,
      currency: (f.currency.value || 'HUF').trim().toUpperCase(),
      is_primary: !!f.is_primary.checked,
    };
    if (payload.account_number === '') {
      this.setState({ bankError: t('me.bank_accounts.errors.account_required') });
      return;
    }
    this.setState({ bankBusy: true, bankError: null, bankNotice: null });
    try {
      await api.post('/me/bank-accounts', payload);
      await this._loadBankAccounts();
      this.setState({ bankBusy: false, bankAdding: false, bankNotice: t('me.bank_accounts.added') });
    } catch (err) {
      this.setState({
        bankBusy: false,
        bankError: err instanceof ApiError ? err.message : t('me.bank_accounts.errors.add'),
      });
    }
  }

  async _setBankPrimary(id) {
    if (!id || this.state.bankBusy) return;
    this.setState({ bankBusy: true, bankError: null });
    try {
      const res = await api.post(`/me/bank-accounts/${encodeURIComponent(id)}/primary`, {});
      this.setState({ bankBusy: false, bankAccounts: res.data || [], bankNotice: t('me.bank_accounts.primary_set') });
    } catch (err) {
      this.setState({
        bankBusy: false,
        bankError: err instanceof ApiError ? err.message : t('me.bank_accounts.errors.update'),
      });
    }
  }

  async _deleteBank(id) {
    if (!id || this.state.bankBusy) return;
    if (!confirm(t('me.bank_accounts.delete_confirm'))) return;
    this.setState({ bankBusy: true, bankError: null });
    try {
      await api.delete(`/me/bank-accounts/${encodeURIComponent(id)}`);
      await this._loadBankAccounts();
      this.setState({ bankBusy: false, bankNotice: t('me.bank_accounts.deleted') });
    } catch (err) {
      this.setState({
        bankBusy: false,
        bankError: err instanceof ApiError ? err.message : t('me.bank_accounts.errors.delete'),
      });
    }
  }

  async _loadInvitations() {
    try {
      const res = await api.get('/invitations');
      this.setState({ invitations: res.data || [] });
    } catch (err) {
      // Surface the underlying error message (and status, for ApiError) so
      // the banner is actually useful when triage is happening from a
      // screenshot — the generic fallback hides exactly the info we need.
      const detail = err instanceof ApiError
        ? `${err.message} (${err.status})`
        : (err && err.message ? `${err.message}` : 'unknown error');
      this.setState({ loadError: `${t('me.errors.load')} — ${detail}` });
    }
  }

  async _regenerate(id) {
    if (this.state.busyId) return;
    this.setState({ busyId: id, error: null });
    try {
      const res = await api.post(`/invitations/${id}/regenerate`);
      const invitations = (this.state.invitations || []).map((i) => i.id === id ? { ...i, expires_at: res.expires_at } : i);
      this.setState({ busyId: null, invitations, revealed: { ...this.state.revealed, [id]: res } });
    } catch (err) {
      this.setState({ busyId: null, error: err instanceof ApiError ? err.message : t('me.errors.regenerate') });
    }
  }

  async _revoke(id) {
    if (this.state.busyId) return;
    const inv = (this.state.invitations || []).find((i) => i.id === id);
    const drafts = inv && inv.draft_tenancies_count ? inv.draft_tenancies_count : 0;
    const msg = drafts > 0
      ? t(drafts === 1 ? 'me.revoke_confirm_with_drafts_one' : 'me.revoke_confirm_with_drafts_other', { count: drafts })
      : t('me.revoke_confirm');
    if (!window.confirm(msg)) return;
    this.setState({ busyId: id, error: null });
    try {
      await api.delete(`/invitations/${id}`);
      const invitations = (this.state.invitations || []).map((i) => i.id === id ? { ...i, status: 'REVOKED', draft_tenancies_count: 0 } : i);
      const revealed = { ...this.state.revealed }; delete revealed[id];
      this.setState({ busyId: null, invitations, revealed });
    } catch (err) {
      this.setState({ busyId: null, error: err instanceof ApiError ? err.message : t('me.errors.revoke') });
    }
  }

  _copy(id) {
    const input = this.el.querySelector(`[data-role="invite-url"][data-invite-id="${id}"]`);
    if (!input) return;
    input.select();
    try { navigator.clipboard.writeText(input.value); } catch { document.execCommand('copy'); }
  }
}
